import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { BarChart3, CalendarIcon, Loader2 } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import axios from 'axios';
import { categorizeTagStats } from '@/constants/complaintTags';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PERIOD_OPTIONS = [
  { value: 'today', label: 'Hoje' },
  { value: 'yesterday', label: 'Ontem' },
  { value: '7days', label: '7 dias' },
  { value: '30days', label: '30 dias' },
];

export const DashboardCharts = ({ chartData, chartColors, complaintTagsStats: initialStats, theme, getToken, companySettings }) => {
  const { dm, cardBg, textPrimary, textMuted } = theme;

  const [tagsPeriod, setTagsPeriod] = useState('30days');
  const [tagsDateRange, setTagsDateRange] = useState({ from: null, to: null });
  const [tagsData, setTagsData] = useState(initialStats);
  const [tagsLoading, setTagsLoading] = useState(false);

  // Sync with parent when initialStats changes (e.g., global period changes)
  useEffect(() => {
    if (initialStats) setTagsData(initialStats);
  }, [initialStats]);

  const fetchTagsData = useCallback(async (period, dateRange) => {
    setTagsLoading(true);
    try {
      const params = { period };
      if (period === 'custom' && dateRange?.from && dateRange?.to) {
        params.start_date = dateRange.from.toISOString();
        params.end_date = dateRange.to.toISOString();
      }
      const res = await axios.get(`${API}/feedbacks/complaint-tags-stats`, {
        headers: { Authorization: `Bearer ${getToken()}` },
        params
      });
      setTagsData(res.data);
    } catch {
      // keep existing data on error
    } finally {
      setTagsLoading(false);
    }
  }, [getToken]);

  const handlePeriodChange = (period) => {
    setTagsPeriod(period);
    setTagsDateRange({ from: null, to: null });
    fetchTagsData(period, null);
  };

  const handleDateRangeSelect = (range) => {
    setTagsDateRange(range || { from: null, to: null });
    if (range?.from && range?.to) {
      setTagsPeriod('custom');
      fetchTagsData('custom', range);
    }
  };

  const periodLabel = tagsPeriod === 'custom' && tagsDateRange.from && tagsDateRange.to
    ? `${format(tagsDateRange.from, 'dd/MM')} - ${format(tagsDateRange.to, 'dd/MM')}`
    : PERIOD_OPTIONS.find(p => p.value === tagsPeriod)?.label || '30 dias';

  return (
    <>
      {/* Ratings by Category Chart */}
      <Card className={`lg:col-span-5 rounded-xl border shadow-sm ${cardBg} transition-colors duration-300`} data-testid="ratings-chart">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className={`text-base font-semibold ${textPrimary}`}>Avaliacao por Categoria</CardTitle>
            <BarChart3 className={`w-5 h-5 ${textMuted}`} />
          </div>
        </CardHeader>
        <CardContent>
          <div className="h-[280px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} layout="vertical" margin={{ left: 0, right: 20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis type="number" domain={[0, 5]} tick={{ fontSize: 12 }} />
                <YAxis dataKey="name" type="category" width={90} tick={{ fontSize: 12 }} />
                <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }} />
                <Bar dataKey="value" radius={[0, 6, 6, 0]}>
                  {chartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={chartColors[index % chartColors.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Top Reclamacoes with Independent Filter */}
      {(tagsData?.tags?.length > 0 || tagsLoading) && (
        <Card className={`lg:col-span-7 rounded-xl border shadow-sm ${cardBg} transition-colors duration-300`} data-testid="complaint-tags-chart">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <CardTitle className={`text-base font-semibold ${textPrimary}`}>Top Reclamacoes</CardTitle>
              <span className={`text-xs ${textMuted}`}>
                {tagsLoading ? 'Carregando...' : `${tagsData?.total_feedbacks_with_tags || 0} avaliacoes`}
              </span>
            </div>

            {/* ═══ Filter Bar ═══ */}
            <div className="flex flex-wrap items-center gap-1.5 mt-3" data-testid="tags-filter-bar">
              {PERIOD_OPTIONS.map(opt => (
                <button
                  key={opt.value}
                  onClick={() => handlePeriodChange(opt.value)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all duration-200 ${
                    tagsPeriod === opt.value
                      ? (dm
                        ? 'bg-[#C5A059] text-[#0A2647]'
                        : 'bg-[#0A2647] text-white')
                      : (dm
                        ? 'bg-slate-700 text-slate-400 hover:bg-slate-600 hover:text-slate-200'
                        : 'bg-slate-100 text-slate-500 hover:bg-slate-200 hover:text-slate-700')
                  }`}
                  data-testid={`tags-filter-${opt.value}`}
                >
                  {opt.label}
                </button>
              ))}

              {/* Custom Date Picker */}
              <Popover>
                <PopoverTrigger asChild>
                  <button
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all duration-200 ${
                      tagsPeriod === 'custom'
                        ? (dm ? 'bg-[#C5A059] text-[#0A2647]' : 'bg-[#0A2647] text-white')
                        : (dm ? 'bg-slate-700 text-slate-400 hover:bg-slate-600' : 'bg-slate-100 text-slate-500 hover:bg-slate-200')
                    }`}
                    data-testid="tags-filter-custom"
                  >
                    <CalendarIcon className="w-3.5 h-3.5" />
                    {tagsPeriod === 'custom' && tagsDateRange.from && tagsDateRange.to
                      ? `${format(tagsDateRange.from, 'dd/MM')} - ${format(tagsDateRange.to, 'dd/MM')}`
                      : 'Período'}
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="end">
                  <Calendar
                    mode="range"
                    selected={tagsDateRange}
                    onSelect={handleDateRangeSelect}
                    locale={ptBR}
                    numberOfMonths={1}
                  />
                </PopoverContent>
              </Popover>
            </div>
          </CardHeader>

          <CardContent className="relative">
            {tagsLoading && (
              <div className="absolute inset-0 bg-white/60 dark:bg-slate-900/60 backdrop-blur-[2px] z-10 flex items-center justify-center rounded-xl" data-testid="tags-loading-overlay">
                <Loader2 className="w-6 h-6 animate-spin text-[#C5A059]" />
              </div>
            )}

            {tagsData?.tags?.length > 0 ? (
              (() => {
                const buckets = categorizeTagStats(tagsData.tags, companySettings);
                return Object.entries(buckets).map(([key, cat]) => {
                  if (!cat.tags.length) return null;
                  const maxCount = cat.tags[0]?.count || 1;
                  return (
                    <div key={key} className="mb-5 last:mb-0">
                      <p className={`text-xs font-bold uppercase tracking-wide mb-2.5 ${textMuted}`}>{cat.label}</p>
                      <div className="space-y-2.5">
                        {cat.tags.map((item, idx) => {
                          const pct = Math.round((item.count / maxCount) * 100);
                          return (
                            <div key={item.tag} data-testid={`tag-stat-${cat.label}-${idx}`}>
                              <div className="flex items-center justify-between mb-0.5">
                                <span className={`text-xs font-medium ${textPrimary}`}>{item.tag}</span>
                                <span className={`text-xs font-bold ${dm ? 'text-red-400' : 'text-red-600'}`}>{item.count}x</span>
                              </div>
                              <div className={`h-2.5 rounded-full ${dm ? 'bg-slate-700' : 'bg-slate-100'}`}>
                                <div className={`h-full rounded-full bg-gradient-to-r ${cat.color} transition-all duration-500`}
                                  style={{ width: `${pct}%` }} />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                });
              })()
            ) : (
              !tagsLoading && (
                <div className="text-center py-8">
                  <BarChart3 className={`w-10 h-10 mx-auto mb-2 ${dm ? 'text-slate-600' : 'text-slate-200'}`} />
                  <p className={`text-sm ${textMuted}`}>Nenhuma reclamacao com tags neste período</p>
                  <p className={`text-xs ${textMuted} mt-1`}>Tente outro intervalo de datas</p>
                </div>
              )
            )}
          </CardContent>
        </Card>
      )}
    </>
  );
};
